import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useMatch, useNavigate } from "react-router-dom";

import { useScrollToFirstError } from "@/apps/partner/hooks/useScrollToFirstError";
import { AddDeviceFormType } from "@/apps/partner/types";
import {
  PARTNER_ADD_DEVICE_BASE,
  PARTNER_ADD_DEVICE_TOTAL_STEPS,
  partnerAddDeviceStepMapping,
  partnerAddDeviceStepPaths,
  partnerAddDeviceSubsteps,
} from "@/apps/partner/utils/partnerAddDevice.constants";
import { PartnerAddDeviceContext } from "@/apps/partner/views/AddDevice/PartnerAddDeviceContext";
import { Button } from "@/components/Interface/Button/Button";
import { StepMenuItem } from "@/components/Interface/Stepper/StepMenu";
import { StepFlowLayout } from "@/components/Layout/BaseLayouts/StepFlowLayout/StepFlowLayout";
import { SCHWEIZ } from "@/constants/countries";
import { SalesPartnerCreateCustomerLanguageEnum } from "@/services/model";

export const AddDevice = () => {
  const { t } = useTranslation("partnerPortal", { keyPrefix: "addDevice" });
  const { t: tCommon } = useTranslation();
  const navigate = useNavigate();
  const routeMatch = useMatch(`${PARTNER_ADD_DEVICE_BASE}/:step/:substep`);
  const [allowedMaxStep, setAllowedMaxStep] = useState(1);

  const formMethods = useForm<AddDeviceFormType>({
    shouldFocusError: false,
    reValidateMode: "onChange",
    mode: "onSubmit",
    defaultValues: {
      simType: undefined,
      productId: undefined,
      mail: "",
      tariffName: "",
      tariffPrice: "",
      tariffSpeed: "",
      quantity: 1,
      customer: {
        salutationEnum: "1",
        languageEnum: SalesPartnerCreateCustomerLanguageEnum.DE,
        firstName: "",
        lastName: "",
        street: "",
        houseNumber: "",
        postalDeliveryNotes: "",
        zip: "",
        city: "",
        countryCodeAlpha2: SCHWEIZ,
        contactPhoneNumber: "",
        birthday: "",
      },
      simAlias: "",
      onp: "false",
    },
  });

  formMethods.register("simType", {
    required: t("step1.required"),
  });

  formMethods.register("productId", {
    required: t("rateSelection.required"),
    valueAsNumber: true,
  });

  useScrollToFirstError(
    formMethods.formState.errors,
    true,
    formMethods.formState.isSubmitting,
    formMethods.formState.submitCount,
  );

  const substep = routeMatch?.params.substep ?? "sim-type";
  const currentStep =
    partnerAddDeviceStepMapping[substep] ??
    partnerAddDeviceStepMapping["sim-type"];

  useEffect(() => {
    if (currentStep.step > allowedMaxStep) {
      const allowedSubstep = partnerAddDeviceSubsteps.find(
        (stepKey) =>
          partnerAddDeviceStepMapping[stepKey].step === allowedMaxStep,
      );

      if (allowedSubstep) {
        navigate(partnerAddDeviceStepPaths[allowedSubstep], { replace: true });
      }
    }
  }, [allowedMaxStep, currentStep.step, navigate]);

  const menuItems: StepMenuItem[] = partnerAddDeviceSubsteps.map((stepKey) => {
    const stepMeta = partnerAddDeviceStepMapping[stepKey];

    return {
      label: t(`steps.${stepMeta.label}` as "steps.sim-type.label"),
      path: partnerAddDeviceStepPaths[stepKey],
      disabled: allowedMaxStep < stepMeta.step,
    };
  });

  const handleCancel = () => {
    navigate("/partner/cockpit");
  };

  if (!routeMatch) {
    return <Navigate to={partnerAddDeviceStepPaths["sim-type"]} replace />;
  }

  return (
    <FormProvider {...formMethods}>
      <PartnerAddDeviceContext.Provider
        value={{ allowedMaxStep, setAllowedMaxStep }}
      >
        <StepFlowLayout
          dataTestid="partner-add-device"
          title={t("title")}
          step={currentStep.step}
          totalSteps={PARTNER_ADD_DEVICE_TOTAL_STEPS}
          stepLabel={t(`steps.${currentStep.label}` as "steps.sim-type.label")}
          description={
            currentStep.description
              ? t(
                  `steps.${currentStep.description}` as "steps.sim-type.description",
                )
              : undefined
          }
          menuItems={menuItems}
          menuToggleLabel={t("stepMenuLabel")}
          headerAction={
            <div className="flex w-full items-center justify-between">
              <Button
                dataTestid="partner-add-device-cancel"
                variant="tertiary"
                onClick={handleCancel}
              >
                {tCommon("Common.label.cancel")}
              </Button>
            </div>
          }
        >
          <Outlet />
        </StepFlowLayout>
      </PartnerAddDeviceContext.Provider>
    </FormProvider>
  );
};
